/**
 * Navigation Component
 */

import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BellAlertIcon, ArrowRightOnRectangleIcon, PlusCircleIcon } from '@heroicons/react/24/outline';
import idpService from '../services/idp';
import { getUserDisplayName, getUserInitials } from '../utils/userDisplay';
import GlobalSearch from './GlobalSearch';
import FavoriteButton from './FavoriteButton';
import './Navigation.css';

function Navigation({ user, onLogout, onToggleSidebar, selectedCompany, onCompanyChange, authorizedCompanies = [], onToggleLauncher, launcherOpen = false, alertsFeed = null, alertsLoading = false, onOpenFeedbackQuickCreate, favorites, onAddFavorite, onRemoveFavorite, favoriteSaving = false, favoriteEnabled = true }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showAlerts, setShowAlerts] = useState(false);
  const [showVersion, setShowVersion] = useState(false);
  const [backendVersion, setBackendVersion] = useState(null);

  useEffect(() => {
    const apiBase = process.env.REACT_APP_API_URL ?? window.__ENV__?.API_URL ?? '';
    fetch(`${apiBase}/api/version`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setBackendVersion(data); })
      .catch(() => {});
  }, []);

  const withFallback = useCallback((key, fallback) => {
    const value = t(key);
    return !value || value === key ? fallback : value;
  }, [t]);

  const userDisplayName = useMemo(() => getUserDisplayName(user), [user]);
  const userInitials = useMemo(() => getUserInitials(user), [user]);

  const companyOptions = useMemo(() => {
    const mapped = (authorizedCompanies || []).map((company) => {
      if (company && typeof company === 'object') {
        const id = company.id || company.slug || company.code || company.company_id || company.value || company.company || '';
        const label = company.name || company.company_name || company.display_name || company.label || company.slug || company.code || company.id || company.company_id || company.company || 'Empresa';
        return { id: id?.toString(), label };
      }
      const id = company?.toString();
      const label = id ? `${withFallback('company.current', 'Empresa')} ${id}` : withFallback('company.current', 'Empresa');
      return { id, label };
    });
    // Orden alfabético (respetando acentos y mayúsculas del español).
    return mapped.sort((a, b) =>
      String(a.label || '').localeCompare(String(b.label || ''), 'es', { sensitivity: 'base', numeric: true })
    );
  }, [authorizedCompanies, withFallback]);

  const currentCompany = useMemo(() => {
    if (!companyOptions.length) return null;
    if (selectedCompany) {
      const found = companyOptions.find((c) => c.id?.toString() === selectedCompany?.toString());
      if (found) return found;
    }
    if (companyOptions.length === 1) return companyOptions[0];
    return null;
  }, [companyOptions, selectedCompany]);

  const handleLogout = async () => {
    if (onLogout) {
      await onLogout();
    } else {
      try {
        // Call CMDB backend logout endpoint
        const apiBase = (process.env.REACT_APP_API_URL ?? window.__ENV__?.API_URL ?? '');
        await fetch(`${apiBase}/api/auth/logout`, {
          method: 'POST',
          credentials: 'include'
        }).catch(() => {
          // Continue even if backend logout fails
        });
        
        // Use IdP service to perform logout
        await idpService.logout();
      } catch (error) {
        console.error('Logout failed:', error);
        // Fallback: clear local storage and redirect
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
    }
  };

  const hasMultipleCompanies = companyOptions.length > 1;
  const alertItems = Array.isArray(alertsFeed?.items) ? alertsFeed.items : [];
  const alertCount = alertsFeed?.summary?.total || 0;
  // Si alguna capacidad no está desplegada (o la BD no está migrada) el sello
  // avisa, para no tener que abrir el panel para saberlo.
  const versionProblems =
    !!backendVersion &&
    (!backendVersion.db_up_to_date ||
      (backendVersion.features || []).some((f) => !f.ok));

  const handleAlertNavigate = (item) => {
    setShowAlerts(false);
    if (item?.server_id) {
      navigate('/infrastructure/servers', { state: { openId: item.server_id } });
      return;
    }
    navigate(item?.route || '/infrastructure/servers');
  };

  return (
    <nav className="navbar-custom">
      <div className="navbar-content">
        <div className="navbar-section navbar-left">
          {/* App Launcher toggle button (⊞ grid icon) */}
          {onToggleLauncher && (
            <button
              className={`navbar-launcher-btn ${launcherOpen ? 'active' : ''}`}
              onClick={onToggleLauncher}
              aria-label="App Launcher"
              title={t('menu.launcher.title', 'Selector de módulos')}
            >
              <svg viewBox="0 0 20 20" fill="currentColor" className="launcher-btn-icon">
                <rect x="2" y="2" width="6" height="6" rx="1.5" />
                <rect x="12" y="2" width="6" height="6" rx="1.5" />
                <rect x="2" y="12" width="6" height="6" rx="1.5" />
                <rect x="12" y="12" width="6" height="6" rx="1.5" />
              </svg>
            </button>
          )}

          {onToggleSidebar && (
            <button
              className="navbar-toggle-mobile"
              onClick={onToggleSidebar}
              aria-label="Toggle sidebar"
            >
              ☰
            </button>
          )}

          {hasMultipleCompanies && (
            <div className="navbar-company">
              <div className="company-selector-wrap">
                <label className="company-chip-label">{withFallback('company.current', 'Empresa')}</label>
                <select 
                  value={selectedCompany || currentCompany?.id || ''}
                  onChange={(e) => onCompanyChange && onCompanyChange(e.target.value)}
                  aria-label={withFallback('company.select', 'Selecciona empresa')}
                  className="company-selector"
                >
                  <option value="">{withFallback('company.select', 'Selecciona empresa')}</option>
                  {companyOptions.map(company => (
                    <option key={company.id || company.label} value={company.id}>
                      {company.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {backendVersion && (
            <div className="navbar-version">
              <button
                type="button"
                className={`navbar-version-badge ${showVersion ? 'active' : ''}`}
                onClick={() => {
                  setShowVersion(!showVersion);
                  setShowAlerts(false);
                }}
                title="Versión desplegada (pulsa para ver el detalle y la comprobación en vivo)"
              >
                v{backendVersion.version}
                <span className="version-ref">{(backendVersion.build_ref || '').slice(0, 7)}</span>
                {versionProblems && <span className="version-warn" title="El despliegue no está al día">!</span>}
              </button>

              {showVersion && (
                <div className="navbar-version-dropdown">
                  <div className="navbar-version-head">
                    <div className="navbar-version-title">
                      v{backendVersion.version}
                      <span className="navbar-version-date">{backendVersion.build_date}</span>
                    </div>
                    <div className="navbar-version-sub">
                      commit <code>{backendVersion.build_ref || '—'}</code>
                    </div>
                    <div className={`navbar-version-db ${backendVersion.db_up_to_date ? 'is-ok' : 'is-bad'}`}>
                      <span>{backendVersion.db_up_to_date ? '✔' : '✘'}</span>
                      <span>
                        Base de datos: <code>{backendVersion.db_revision || '—'}</code>
                        {backendVersion.db_expected_revision &&
                          backendVersion.db_revision !== backendVersion.db_expected_revision && (
                            <> · esperada <code>{backendVersion.db_expected_revision}</code> (falta <code>flask db upgrade</code>)</>
                          )}
                      </span>
                    </div>
                  </div>

                  {(backendVersion.release_notes || []).slice(0, 1).map((rel) => (
                    <div className="navbar-version-section" key={rel.ref || rel.date}>
                      <div className="navbar-version-section-title">Qué incluye esta versión</div>
                      <div className="navbar-version-release-title">{rel.title}</div>
                      <ul className="navbar-version-list">
                        {(rel.items || []).map((it, i) => <li key={i}>{it}</li>)}
                      </ul>
                    </div>
                  ))}

                  {(backendVersion.features || []).length > 0 && (
                    <div className="navbar-version-section">
                      <div className="navbar-version-section-title">Comprobación en vivo</div>
                      <ul className="navbar-version-features">
                        {(backendVersion.features || []).map((f) => (
                          <li key={f.key} className={f.ok ? 'is-ok' : 'is-bad'}>
                            <span className="navbar-version-mark">{f.ok ? '✔' : '✘'}</span>
                            <span>{f.label}</span>
                          </li>
                        ))}
                      </ul>
                      <div className="navbar-version-hint">
                        Las capacidades se comprueban contra el código del servidor que está corriendo:
                        si algo sale en rojo, el backend desplegado no está al día. Y el hecho de que
                        veas este panel ya confirma que el frontend es el nuevo.
                      </div>
                    </div>
                  )}

                  {(backendVersion.release_notes || []).length > 1 && (
                    <details className="navbar-version-past">
                      <summary>Versiones anteriores</summary>
                      {backendVersion.release_notes.slice(1).map((rel) => (
                        <div className="navbar-version-release" key={rel.ref || rel.date}>
                          <div className="navbar-version-release-title">
                            <code>{(rel.ref || '').slice(0, 7)}</code> · {rel.date} · {rel.title}
                          </div>
                          <ul className="navbar-version-list">
                            {(rel.items || []).map((it, i) => <li key={i}>{it}</li>)}
                          </ul>
                        </div>
                      ))}
                    </details>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Buscador global: por defecto acotado a la empresa activa; con el
            conmutador «Todas» busca en todos los tenants del usuario y cambia
            de empresa al abrir un resultado de otro. */}
        <GlobalSearch
          companyId={selectedCompany}
          companies={companyOptions}
          onCompanyChange={onCompanyChange}
        />

        <div className="navbar-section navbar-right">
          {/* Estrella de la página actual: el acceso directo se añade desde
              aquí en cualquier listado o ficha, sin botón por pantalla. */}
          {onAddFavorite && (
            <FavoriteButton
              favorites={favorites}
              onAdd={onAddFavorite}
              onRemove={onRemoveFavorite}
              saving={favoriteSaving}
              enabled={favoriteEnabled}
            />
          )}

          <button
            className="navbar-action-btn navbar-tour-btn"
            onClick={() => navigate('/academia')}
            title="Academia: la guía de la CMDB, con capturas y sandboxes"
            type="button"
          >
            <span className="navbar-tour-icon" aria-hidden="true">🎓</span>
            <span className="action-label">{t('nav.academy', 'Academia')}</span>
          </button>

          <button
            className="navbar-action-btn navbar-feedback-btn"
            onClick={() => onOpenFeedbackQuickCreate && onOpenFeedbackQuickCreate()}
            title="Nuevo ticket de feedback"
            type="button"
          >
            <PlusCircleIcon className="navbar-feedback-icon" />
            <span className="action-label">{t('nav.newTicket', 'Nuevo ticket')}</span>
          </button>

          <div className="navbar-alerts">
            <button
              className={`navbar-action-btn navbar-alerts-btn ${showAlerts ? 'active' : ''}`}
              onClick={() => {
                setShowAlerts(!showAlerts);
                setShowVersion(false);
              }}
              title={t('nav.alerts', 'Avisos')}
              type="button"
            >
              <BellAlertIcon className="navbar-alert-icon" />
              <span className="action-label">{t('nav.alerts', 'Avisos')}</span>
              {alertCount > 0 && <span className="navbar-alert-badge">{alertCount > 9 ? '9+' : alertCount}</span>}
            </button>
            {showAlerts && (
              <div className="navbar-alerts-dropdown">
                <div className="navbar-alerts-header">
                  <div>
                    <div className="navbar-alerts-title">{t('nav.recentEvents', 'Eventos recientes')}</div>
                    <div className="navbar-alerts-subtitle">
                      {alertsFeed?.summary?.offline_agents || 0} agente(s) offline · {alertsFeed?.summary?.storage_advisories || 0} aviso(s) de storage
                    </div>
                  </div>
                </div>
                <div className="navbar-alerts-list">
                  {alertsLoading && alertItems.length === 0 && (
                    <div className="navbar-alerts-empty">{t('nav.loadingAlerts', 'Cargando avisos…')}</div>
                  )}
                  {!alertsLoading && alertItems.length === 0 && (
                    <div className="navbar-alerts-empty">{t('nav.noAlerts', 'No hay avisos recientes para esta empresa.')}</div>
                  )}
                  {alertItems.map((item, index) => (
                    <button
                      key={`${item.kind || 'alert'}-${item.server_id || 'global'}-${item.timestamp || index}`}
                      className={`navbar-alert-item navbar-alert-item--${item.severity || 'info'}`}
                      onClick={() => handleAlertNavigate(item)}
                      type="button"
                    >
                      <span className="navbar-alert-item-title">{item.title}</span>
                      <span className="navbar-alert-item-message">{item.server_name ? `${item.server_name} · ` : ''}{item.message}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="navbar-user-menu">
            <button
              type="button"
              className="navbar-avatar-btn"
              id="userDropdown"
              onClick={() => {
                setShowAlerts(false);
                setShowVersion(false);
                navigate('/perfil');
              }}
              title={`${userDisplayName}${user?.email ? ` · ${user.email}` : ''} — ${withFallback('profile.open', 'Mi perfil')}`}
              aria-label={withFallback('profile.open', 'Mi perfil')}
            >
              <span className="navbar-avatar-initials">{userInitials}</span>
            </button>
            <button
              type="button"
              className="navbar-logout-btn"
              onClick={handleLogout}
              title={t('action.logout', 'Cerrar sesión')}
              aria-label={t('action.logout', 'Cerrar sesión')}
            >
              <ArrowRightOnRectangleIcon className="navbar-logout-icon" />
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
}

export default Navigation;
